import moment from "moment-timezone";

/** One row of the lift-faults table, built from a POST /reports/faults/recent item. */
export interface FaultRow {
  id: string;
  created_at: string;
  short_msg: string;
  duration: string;
}

// Formats the API may return for date/cleared timestamps
const FAULT_DATE_FORMATS = ["ddd. MMM DD. YYYY - HH:mm:ss", moment.ISO_8601];

/** Format a second count as `HH:mm:ss`, prefixed with `<days>d ` from one day up. */
function formatElapsed(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/**
 * Return the display value for a fault duration.
 * - A duration other than "Active" is returned as-is.
 * - With no cleared timestamp (or "Active"), the fault is still active:
 *   "Active - <elapsed>" when `date` parses and is not in the future, else "Active".
 * - Otherwise the time from `date` to `cleared`, or "" when `date` is missing,
 *   either does not parse, or `cleared` is before `date`.
 */
export function formatFaultDuration(
  duration: string | null | undefined,
  cleared: string | null | undefined,
  date?: string | null,
): string {
  if (duration && duration !== "Active") return duration;
  if (!cleared || cleared === "Active") {
    if (date) {
      const start = moment(date, FAULT_DATE_FORMATS);
      if (start.isValid()) {
        const totalSeconds = Math.floor(moment().diff(start) / 1000);
        if (totalSeconds >= 0) return `Active - ${formatElapsed(totalSeconds)}`;
      }
    }
    return "Active";
  }
  if (date) {
    const start = moment(date, FAULT_DATE_FORMATS);
    const end = moment(cleared, FAULT_DATE_FORMATS);
    if (start.isValid() && end.isValid() && end.isSameOrAfter(start)) {
      const totalSeconds = Math.floor(end.diff(start) / 1000);
      return formatElapsed(totalSeconds);
    }
  }
  return "";
}

/** Build the request body for POST /reports/faults/recent */
export function buildLiftFaultRequestBody(
  liftSelected: { bankId: any; id: any },
  timezone: string,
  weeks: number = 2,
  limit: number = 20,
): object {
  return {
    bank_id: String(liftSelected.bankId ?? ""),
    lift_id: String(liftSelected.id ?? ""),
    weeks,
    timezone,
    download: "false",
    limit,
  };
}

/** Map API response items to table rows */
export function buildLiftFaultsData(response: any[]): FaultRow[] {
  return response.map((item: any) => ({
    id: `${item.lift_name}-${item.date}-${item.floor}-${item.detailed_fault}-${item.direction}`,
    name: item.lift_name,
    created_at: item.date,
    short_msg: item.detailed_fault,
    duration: formatFaultDuration(item.duration, item.cleared, item.date),
  }));
}
